import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { EquipmentTagPicker } from "@/components/equipment/EquipmentTagPicker";
import { useAppSetting, useUpdateAppSetting } from "@/hooks/useAppSettings";
import type { CameraPlanEquipmentColumnConfig } from "@/hooks/useCameraPlanEquipmentColumns";

type ColumnDraft = { key: string; label: string; tagIds: string[] };

export default function AdminSettingsPage() {
  const { data: saved, isLoading } = useAppSetting<
    CameraPlanEquipmentColumnConfig[]
  >("camera_plan_columns");
  const updateSetting = useUpdateAppSetting();

  const [columns, setColumns] = useState<ColumnDraft[]>([]);
  const [dirty, setDirty] = useState(false);

  // Seed local state from saved setting
  useEffect(() => {
    if (Array.isArray(saved) && !dirty) {
      setColumns(saved.map((c) => ({ ...c })));
    }
  }, [saved, dirty]);

  const addColumn = () => {
    setColumns((prev) => [
      ...prev,
      { key: `col_${Date.now()}`, label: "", tagIds: [] },
    ]);
    setDirty(true);
  };

  const removeColumn = (index: number) => {
    setColumns((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const updateColumn = (index: number, patch: Partial<ColumnDraft>) => {
    setColumns((prev) =>
      prev.map((col, i) => (i === index ? { ...col, ...patch } : col)),
    );
    setDirty(true);
  };

  const handleSave = () => {
    // Generate stable keys from labels if key is auto-generated
    const value = columns.map((col) => ({
      key: col.key,
      label: col.label,
      tagIds: col.tagIds,
    }));
    updateSetting.mutate(
      { key: "camera_plan_columns", value },
      { onSuccess: () => setDirty(false) },
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader title="Settings" />

      <Card>
        <CardHeader>
          <CardTitle>Camera Plan Equipment Columns</CardTitle>
          <CardDescription>
            Configure which equipment columns appear in camera plan tables. Each
            column filters equipment by the selected tags.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : (
            <>
              {columns.map((col, i) => (
                <div
                  key={col.key}
                  data-testid={`equipment-column-${i}`}
                  className="flex items-start gap-3 rounded-lg border p-3"
                >
                  <div className="flex-1 space-y-2">
                    <div>
                      <Label className="text-xs">Column label</Label>
                      <Input
                        data-testid={`equipment-column-label-${i}`}
                        value={col.label}
                        onChange={(e) =>
                          updateColumn(i, { label: e.target.value })
                        }
                        placeholder="e.g. Camera"
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Equipment tags</Label>
                      <EquipmentTagPicker
                        selectedTagIds={col.tagIds}
                        onChange={(tagIds) => updateColumn(i, { tagIds })}
                        placeholder="Select equipment tags..."
                      />
                    </div>
                  </div>
                  <Button
                    data-testid={`equipment-column-remove-${i}`}
                    variant="ghost"
                    size="icon"
                    className="mt-6 h-8 w-8 text-destructive"
                    onClick={() => removeColumn(i)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              <Button
                data-testid="add-equipment-column"
                variant="outline"
                size="sm"
                onClick={addColumn}
              >
                <Plus className="mr-1 h-4 w-4" />
                Add column
              </Button>

              <div className="flex justify-end pt-2">
                <Button
                  data-testid="save-equipment-columns"
                  onClick={handleSave}
                  disabled={updateSetting.isPending || !dirty}
                >
                  {updateSetting.isPending ? "Saving..." : "Save"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
